import type { TaskExecutionArtifact } from "@calcplan/ai-capabilities";

export function normalizeCreationInstanceKey(value: unknown): string {
  return typeof value === "string" && value.trim() ? value.trim().toLocaleLowerCase("pt-BR") : "primary";
}

export function findReusableCreation(
  artifacts: readonly TaskExecutionArtifact[],
  input: { stepId: string; catalogItemId: string; instanceKey: string },
  liveElementIds: ReadonlySet<string>,
): TaskExecutionArtifact | null {
  return artifacts.find((artifact) => artifact.status === "live"
    && artifact.stepId === input.stepId
    && artifact.catalogItemId === input.catalogItemId
    && artifact.instanceKey === input.instanceKey
    && liveElementIds.has(artifact.elementId)) ?? null;
}

export function registerCreation(
  artifacts: readonly TaskExecutionArtifact[],
  artifact: TaskExecutionArtifact,
): TaskExecutionArtifact[] {
  return [
    ...artifacts.filter((candidate) => !(
      candidate.stepId === artifact.stepId
      && candidate.catalogItemId === artifact.catalogItemId
      && candidate.instanceKey === artifact.instanceKey
    )),
    artifact,
  ];
}

export function markCreationsDeleted(
  artifacts: readonly TaskExecutionArtifact[],
  deletedIds: ReadonlySet<string>,
): TaskExecutionArtifact[] {
  return artifacts.map((artifact) => deletedIds.has(artifact.elementId)
    ? { ...artifact, status: "deleted" as const }
    : artifact);
}

export function refreshCreationLedger(
  artifacts: readonly TaskExecutionArtifact[],
  elements: ReadonlyArray<{
    id: string;
    name: string;
    transform: { translationMm: [number, number, number] };
  }>,
): TaskExecutionArtifact[] {
  const byId = new Map(elements.map((element) => [element.id, element]));
  return artifacts.map((artifact) => {
    const element = byId.get(artifact.elementId);
    return element
      ? {
          ...artifact,
          name: element.name,
          status: "live" as const,
          translationMm: [...element.transform.translationMm] as [number, number, number],
        }
      : { ...artifact, status: "deleted" as const };
  });
}

export function liveArtifactsForStep(
  artifacts: readonly TaskExecutionArtifact[],
  stepId: string | null,
): TaskExecutionArtifact[] {
  return artifacts.filter((artifact) => artifact.status === "live" && (!stepId || artifact.stepId === stepId));
}
